import { useMemo, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { useImmer } from 'use-immer'
import { Button, FormControl, FormLabel, Input, Link, Text, VStack } from '@chakra-ui/react'
import { getAssetUrl } from 'lib'

export const InstallationVendorFormType = {
  Add: 'add',
  Edit: 'edit'
}

export const buildInstallationVendorFormData = (form) => {
  const fd = new FormData()
  fd.append('name', form.name || '')
  fd.append('contact', form.contact || '')
  fd.append('email', form.email || '')
  fd.append('phone', form.phone || '')
  fd.append('address', form.address || '')
  fd.append('city', form.city || '')
  fd.append('state', form.state || '')
  fd.append('zip', form.zip || '')
  fd.append('serviceRadiusMiles', form.serviceRadiusMiles ?? '')
  fd.append('coiExpiresAt', form.coiExpiresAt || '')
  if (form.coiFile) fd.append('coi', form.coiFile)
  if (form.w9File) fd.append('w9', form.w9File)
  return fd
}

const toDateInputValue = (value) => {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  return d.toISOString().slice(0, 10)
}

export const InstallationVendorForm = ({ type, onSubmit, isSubmitting, initialForm = null }) => {
  const coiRef = useRef(null)
  const w9Ref = useRef(null)
  const [form, setForm] = useImmer({
    name: '',
    contact: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    serviceRadiusMiles: '',
    coiExpiresAt: '',
    coiPath: '',
    w9Path: '',
    coiFile: null,
    w9File: null
  })

  const buttonText = useMemo(() => {
    switch (type) {
      case InstallationVendorFormType.Add:
        return 'Add Vendor'
      case InstallationVendorFormType.Edit:
        return 'Save Vendor'
    }
  }, [type])

  useEffect(() => {
    if (!initialForm) return
    setForm(draft => {
      draft.name = initialForm.name ?? ''
      draft.contact = initialForm.contact ?? ''
      draft.email = initialForm.email ?? ''
      draft.phone = initialForm.phone ?? ''
      draft.address = initialForm.address ?? ''
      draft.city = initialForm.city ?? ''
      draft.state = initialForm.state ?? ''
      draft.zip = initialForm.zip ?? ''
      draft.serviceRadiusMiles = initialForm.serviceRadiusMiles ?? ''
      draft.coiExpiresAt = toDateInputValue(initialForm.coiExpiresAt)
      draft.coiPath = initialForm.coiPath ?? ''
      draft.w9Path = initialForm.w9Path ?? ''
    })
  }, [initialForm])

  const handleChange = (e) => {
    setForm(draft => {
      draft[e.target.name] = e.target.value
    })
  }

  const handleFileChange = (e) => {
    const { name, files } = e.target
    setForm(draft => {
      draft[name] = files?.[0] || null
    })
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    onSubmit(form)
  }

  return (
    <form onSubmit={handleSubmit}>
      <VStack gap={2} maxW="500px">
        <FormControl isRequired>
          <FormLabel>Name</FormLabel>
          <Input name="name" value={form.name} onChange={handleChange} />
        </FormControl>
        <FormControl>
          <FormLabel>Contact Name</FormLabel>
          <Input name="contact" value={form.contact} onChange={handleChange} />
        </FormControl>
        <FormControl isRequired>
          <FormLabel>Email</FormLabel>
          <Input name="email" value={form.email} onChange={handleChange} />
        </FormControl>
        <FormControl isRequired>
          <FormLabel>Phone</FormLabel>
          <Input name="phone" value={form.phone} onChange={handleChange} />
        </FormControl>
        <FormControl isRequired>
          <FormLabel>Address</FormLabel>
          <Input name="address" value={form.address} onChange={handleChange} />
        </FormControl>
        <FormControl isRequired>
          <FormLabel>City</FormLabel>
          <Input name="city" value={form.city} onChange={handleChange} />
        </FormControl>
        <FormControl isRequired>
          <FormLabel>State</FormLabel>
          <Input name="state" value={form.state} onChange={handleChange} />
        </FormControl>
        <FormControl isRequired>
          <FormLabel>Zip</FormLabel>
          <Input name="zip" value={form.zip} onChange={handleChange} />
        </FormControl>
        <FormControl>
          <FormLabel>Service Radius (miles)</FormLabel>
          <Input type="number" name="serviceRadiusMiles" value={form.serviceRadiusMiles} onChange={handleChange} />
        </FormControl>
        <FormControl>
          <FormLabel>COI (Certificate of Insurance)</FormLabel>
          <Input ref={coiRef} type="file" name="coiFile" onChange={handleFileChange} hidden />
          <Button type="button" size="sm" onClick={() => coiRef.current?.click()}>Choose File</Button>
          <Text as="span" ml={2} fontSize="sm">
            {form.coiFile?.name || (form.coiPath ? <Link href={getAssetUrl(form.coiPath)} isExternal color="blue.500">View current</Link> : 'No file selected')}
          </Text>
        </FormControl>
        <FormControl>
          <FormLabel>COI Expiration Date</FormLabel>
          <Input type="date" name="coiExpiresAt" value={form.coiExpiresAt} onChange={handleChange} />
        </FormControl>
        <FormControl>
          <FormLabel>W-9</FormLabel>
          <Input ref={w9Ref} type="file" name="w9File" onChange={handleFileChange} hidden />
          <Button type="button" size="sm" onClick={() => w9Ref.current?.click()}>Choose File</Button>
          <Text as="span" ml={2} fontSize="sm">
            {form.w9File?.name || (form.w9Path ? <Link href={getAssetUrl(form.w9Path)} isExternal color="blue.500">View current</Link> : 'No file selected')}
          </Text>
        </FormControl>
      </VStack>
      <Button type="submit" mt={8} colorScheme="blue" isLoading={isSubmitting}>{buttonText}</Button>
    </form>
  )
}

InstallationVendorForm.propTypes = {
  type: PropTypes.oneOf(Object.values(InstallationVendorFormType)).isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  initialForm: PropTypes.object
}
